import { useTranslation } from "react-i18next";
import { ColorDot } from "components/charts";
import type { Granularity } from "components/charts";
import {
  formatBucketAxisLabel,
  formatBucketDate,
  formatCurrency,
  formatNumber,
  formatPercent,
} from "utils/formatting";
import { Text } from "components/text";
import { AmazonSalesTooltipRow as Row } from "./AmazonSalesTooltipRow";

/**
 * Hover tooltip for the Amazon Sales chart. Metrics without data render an
 * em dash rather than a zero. The rank row is granularity-aware: "Rank (day)"
 * on daily, "Best Rank (date)" on weekly/monthly, where the date is the day the
 * bucket's best rank was observed.
 */
export const AmazonSalesTooltip = ({
  date,
  units,
  revenue,
  pageViews,
  conversionRate,
  salesRank,
  salesRankDate,
  currency,
  granularity,
  isPreorder = false,
}: {
  /** Bucket start, ISO date (YYYY-MM-DD). */
  date: string;
  units: number | null;
  revenue: number | null;
  pageViews: number | null;
  conversionRate: number | null;
  salesRank: number | null;
  /** Day the bucket's best rank was observed, ISO date; null when no rank. */
  salesRankDate: string | null;
  /** ISO 4217 currency for the revenue row; null hides the amount. */
  currency: string | null;
  granularity: Granularity;
  isPreorder?: boolean;
}) => {
  const { t } = useTranslation(["productDetails"]);

  const dash = "—";
  const number = (value: number | null) =>
    value == null ? dash : formatNumber(value);

  const rankLabel =
    salesRank == null || salesRankDate == null
      ? t("productDetails:salesData.amazonSales.tooltip.rank")
      : t(
          granularity === "daily"
            ? "productDetails:salesData.amazonSales.tooltip.rankDay"
            : "productDetails:salesData.amazonSales.tooltip.rankBest",
          { date: formatBucketAxisLabel(salesRankDate, "daily") },
        );

  return (
    <div className="grid min-w-[11rem] gap-1.5 rounded-lg bg-secondary-800 px-3 py-2 text-xs text-white shadow-xl">
      <div className="flex items-center justify-between gap-2">
        <Text as="span" variant="tooltip">
          {formatBucketDate(date)}
        </Text>
      </div>
      <Row
        indicator={
          <ColorDot
            className={isPreorder ? "bg-(--chart-6-muted)" : "bg-(--chart-6)"}
            ring
          />
        }
        label={t(
          isPreorder
            ? "productDetails:salesData.amazonSales.tooltip.preorders"
            : "productDetails:salesData.amazonSales.tooltip.units",
        )}
        value={number(units)}
      />
      <Row
        indicator={<span aria-hidden className="h-0.5 w-2 bg-(--chart-1)" />}
        label={t("productDetails:salesData.amazonSales.tooltip.conversion")}
        value={conversionRate == null ? dash : formatPercent(conversionRate)}
      />
      <Row
        indicator={
          <span
            aria-hidden
            className="w-2 border-t-2 border-dashed border-(--chart-3)"
          />
        }
        label={rankLabel}
        value={salesRank == null ? dash : `#${formatNumber(salesRank)}`}
      />
      <div className="grid gap-1.5 border-t border-white/10 pt-1.5">
        <Row
          label={t("productDetails:salesData.amazonSales.tooltip.sales")}
          value={
            revenue == null || !currency
              ? dash
              : formatCurrency(revenue, currency)
          }
        />
        <Row
          label={t("productDetails:salesData.amazonSales.tooltip.pageViews")}
          value={number(pageViews)}
        />
      </div>
    </div>
  );
};
